/**
 * US 12.5 — a call with a Hub's or Squad's members, started from Tasks.
 *
 * Tasks owns the button and the member list; the Calls module owns the call.
 * `startTeamCall` opens a room named after the team (TRUSTED and guest-free,
 * like a direct call, so the people rung in skip the lobby and nobody else
 * can walk in) and leaves the member list for the call screen, which rings
 * exactly those people once the caller has joined.
 */
import { usersService } from "../../chat/api/users";
import { videoService } from "../api";

const key = (roomId: string) => `weco.call.teamInvite.${roomId}`;

export interface TeamInvite {
  name: string;
  emails: string[];
}

export async function startTeamCall(name: string, memberEmails: string[], myEmail: string): Promise<string> {
  const room = await videoService.createRoom({
    name,
    is_public: false,
    allow_guests: false,
    waiting_room_enabled: false,
    admit_policy: "owners",
    access_level: "trusted",
  });
  const me = myEmail.trim().toLowerCase();
  const emails = [...new Set(memberEmails.map((e) => e.trim().toLowerCase()))].filter((e) => e && e !== me);
  try {
    sessionStorage.setItem(key(room.id), JSON.stringify({ name, emails } satisfies TeamInvite));
  } catch {
    /* storage blocked: the call still opens, members can be rung by hand */
  }
  return room.id;
}

/** The members waiting to be rung into this room — read once, then forgotten. */
export function takeTeamInvite(roomId: string): TeamInvite | null {
  try {
    const raw = sessionStorage.getItem(key(roomId));
    if (!raw) return null;
    sessionStorage.removeItem(key(roomId));
    const parsed = JSON.parse(raw) as TeamInvite;
    return Array.isArray(parsed.emails) ? parsed : null;
  } catch {
    return null;
  }
}

/** Member emails → directory accounts (the ring endpoint takes subs). */
export async function resolveMembers(emails: string[]): Promise<{ sub: string; name: string }[]> {
  const found = await Promise.all(
    emails.map(async (email) => {
      try {
        const page = await usersService.list({ search: email, limit: 5 });
        const hit = page.results.find((u) => (u.email ?? "").toLowerCase() === email);
        return hit ? { sub: hit.sub, name: hit.name || email } : null;
      } catch {
        return null;
      }
    }),
  );
  return found.filter((u): u is { sub: string; name: string } => u !== null);
}
